import { CommonModule, DatePipe, Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { PatientService } from '../patient/patient.service';
import { IPatientInfo } from '../patient/ipatient-info';
import { GenderList } from 'src/app/common/types/common';
import { environment } from 'src/environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { IpdFormComponent } from './ipd-form/ipd-form.component';
import { OPDService } from '../opd/opd.service';
import { Iopd } from '../opd/opddetails/iopddetails';
import { UserService } from '../user/user.service';
import { IUsers } from '../user/iusers';
import { catchError, finalize, forkJoin, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { MedicalReportType } from 'src/app/common/enums/medical-report-type';
import { IpdService } from './ipd.service';
import { Iipd, IoperationSummary } from './Iipd.type';
import { MatTableDataSource } from '@angular/material/table';
import { HttpClient } from '@angular/common/http';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { TimeFormatPipe } from 'src/app/pipe/time.pipe';
import { FallbackImageDirective } from 'src/app/common/directive/fallback-image.directive';
import { MatDialog } from '@angular/material/dialog';
import { IpdOperationSummaryComponent } from './ipd-operation-summary/ipd-operation-summary.component';
import { AddBillChargeComponent } from '../dialogs/bill/bill.component';
import { BillType } from 'src/app/common/enums/bill-type';
import { IPDBillData } from '../dialogs/bill/ipd-bill-data.interface';
import { BillDialogData } from '../dialogs/bill/bill-dialog-data.interface';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { IpdDischargeComponent } from './ipd-discharge/ipd-discharge.component';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { LoaderComponent } from 'src/app/shared/loader/loader.component';
import { PrescriptionComponent } from '../opd/prescription/prescription.component';
import { MedicalCertificateComponent } from './medical-certificate/medical-certificate.component';
import { PatientViewComponent } from '../patient/patient-view/patient-view.component';
import { PrintSettingService } from '../settings/hospital-setting/print-setting/print-setting.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';
import { MedicalRecordDetailComponent } from '../patient/patient-view/medical-record/medical-record-detail/medical-record-detail.component';
import { MedicalRecordComponent } from '../patient/patient-view/medical-record/medical-record.component';

@Component({
  selector: 'app-ipd',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    TimeFormatPipe,
    FallbackImageDirective,
    LoaderComponent,
    DatePipe,
    IpdDischargeComponent,
    MedicalRecordComponent
  ],
  providers: [
    DatePipe,
    TimeFormatPipe,
    DefaultValuePipe
  ],
  templateUrl: './ipd.component.html',
  styleUrl: './ipd.component.scss'
})
export class IpdComponent implements OnInit, OnDestroy {

  patientinfo: IPatientInfo;
  staticimagepath: string = environment.imagePath;
  defaultAvatarPath = 'assets/images/profile/user-1.jpg';
  OpdData: Iopd;
  doctorDetails: IUsers;
  ipdData: Iipd;
  operationSummary: IoperationSummary | null = null;
  @ViewChild(IpdFormComponent) ipdFormComponent!: IpdFormComponent;
  @ViewChild(MedicalRecordComponent) medicalRecordComponent!: MedicalRecordComponent;
  displayedColumns: string[] = [
    'days',
    'date',
    'time',
    'diagnosis',
    'treatment',
  ];
  dataSource = new MatTableDataSource<any>();
  showPatientInfo: boolean = false;
  medicalRecordCount: number = 0;
  private destroy$ = new Subject<void>();
  ipdDetails: Iipd;
  @ViewChild('reportPrintComponent') reportPrintComponent!: IpdDischargeComponent;

  constructor(
    private route: ActivatedRoute,
    private patientService: PatientService,
    private location: Location,
    private opdService: OPDService,
    private userService: UserService,
    private ipdService: IpdService,
    private http: HttpClient,
    private hospitalState: HospitalStateService,
    private datePipe: DatePipe,
    private timeFormatPipe: TimeFormatPipe,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private loaderService: LoaderService,
    private router: Router,
    private printSettingService: PrintSettingService,
    private defaultValuePipe: DefaultValuePipe
  ) { }

  ngOnInit(): void {
    // Load patient
    this.loadPatient();
    this.loadIpdData();
  }

  goBack() {
    this.location.back();
  }

  getGenderName(value?: string): string {
    const gender = GenderList.find(g => g.value === value);
    return gender ? gender.name : '';
  }

  get profileImagePathSrc(): string {
    if (this.patientinfo?.profileImagePath) {
      return this.staticimagepath + this.patientinfo.profileImagePath;
    }
    return this.defaultAvatarPath;
  }

  async printAdmissionPaper() {
    if (!this.ipdData || !this.OpdData) {
      return;
    }
    // Open dialog with IPD form in edit mode
    const dialogRef = this.dialog.open(IpdFormComponent, {
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        OPDDetails: this.OpdData,
        existingIpdData: this.ipdData,
        readOnly: this.hasDischargeDate(),
        onPrint: () => {
          // Print current form data (will be updated data if user updated)
          this.printAdmissionPaperWithCurrentData();
        }
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.updated) {
        // Reload IPD data after update
        this.loadIpdData();
      }
    });
  }

  async printAdmissionPaperWithCurrentData() {
    try {
      this.loaderService.show();
      const printContent = await this.createPrintContent();
      this.openPrintWindow(printContent);
    } catch (error) {
      console.error('Error printing admission paper:', error);
      this.toastr.error('Error loading data for printing');
    } finally {
      this.loaderService.hide();
    }
  }

  private async createPrintContent(): Promise<string> {
    try {
      let template =
        (await this.http
          .get('assets/print-template.html', { responseType: 'text' })
          .toPromise()) || '';

      const hospital = this.hospitalState.getHospital();
      const doctorId = this.OpdData.doctorId;

      const [doctor, settings] = await Promise.all([
        this.userService.getUserByID(doctorId).toPromise(),
        this.printSettingService.getPrintSettingPromise(hospital.id)
      ]);

      const doctorName = `${doctor?.result?.firstName ?? ''} ${doctor?.result?.lastName ?? ''}`.trim();
      const hospitalDetails = hospital || {};
      const patientImageSrc = this.patientinfo.profileImagePath
        ? this.staticimagepath + this.patientinfo.profileImagePath
        : ``;

      const patientImageHtml = `
          <div id="patient-image-box" style="width:90px;">
            <img src="${patientImageSrc}"
                style="width:90px; height:100px; border:1px solid #000;"
                onerror="this.parentElement.style.display='none'" />
          </div>
        `;

      const admissionHtml = `
      <h2 style="text-align:center; margin:10px 0; text-decoration:underline;">
        ADMISSION PAPER
      </h2>

      <div style="display:flex; gap:12px; border:1px solid #000; padding:8px; margin:10px 0;">
        ${patientImageHtml}
        <div style="flex:1; font-size:13px;">
          <strong>Patient Name:</strong>
          ${this.patientinfo.firstName} ${this.patientinfo.lastName}<br/>
          <strong>Address:</strong>
          ${this.patientinfo.address?.[0]?.address1 || '-'}<br/>
          <strong>Ph:</strong>
          ${this.patientinfo.contactInfo?.[0]?.contact || '-'}
        </div>
      </div>

      <table style="width:100%; border-collapse:collapse; margin-top:18px; font-size:13px;">
        <tr>
          <td style="border:1px solid #000; padding:6px;">
            <strong>O.P.D No:</strong> ${this.OpdData.opdNumber}
          </td>
          <td style="border:1px solid #000; padding:6px;">
            <strong>O.P.D Date:</strong>
            ${this.datePipe.transform(this.OpdData.opdDate, 'dd-MM-yyyy') || ''}
          </td>
          <td style="border:1px solid #000; padding:6px;">
            <strong>Bed No:</strong> ${this.ipdData.bedNo}
          </td>
        </tr>

        <tr>
          <td style="border:1px solid #000; padding:6px;">
            <strong>I.P.D No:</strong> ${this.ipdData.ipdNumber}
          </td>
          <td style="border:1px solid #000; padding:6px;">
            <strong>I.P.D Date:</strong>
            ${this.datePipe.transform(this.ipdData.ipdDate, 'dd-MM-yyyy') || ''}
          </td>
          <td style="border:1px solid #000; padding:6px;">
            <strong>Ward:</strong> ${this.ipdData.wardName}
          </td>
        </tr>

        <tr>
          <td style="border:1px solid #000; padding:6px;">
            <strong>Mediclaim:</strong> ${this.ipdData?.mediclaimName || '-'}
          </td>
          <td style="border:1px solid #000; padding:6px;">
            <strong>Policy No:</strong> ${this.ipdData?.policyNumber || '-'}
          </td>
          <td style="border:1px solid #000; padding:6px;">
          </td>
        </tr>
      </table>

      <div style="margin-top:15px; font-size:13px;">
        <strong>Name & Address of relative / friend attending patient:</strong><br/>
        ${this.ipdData.nameAndAddress || '-'}<br/>
        <strong>Tel No:</strong> ${this.ipdData.contact || '-'}
      </div>

      <div style="margin-top:15px; font-size:13px;">
        <strong>Admitted On:</strong>
        ${this.datePipe.transform(this.ipdData.ipdDate, 'dd-MM-yyyy') || ''}
        &nbsp;&nbsp;
        <strong>At:</strong>${this.timeFormatPipe.transform(this.ipdData.ipdTime)}
      </div>

      <div style="margin-top:15px; font-size:13px;">
        <strong>Diagnosis:</strong><br/>
        ${this.ipdData.provisionalDiagnosis || ''}
      </div>

      <div style="margin-top:100px; display:flex; justify-content:space-between; font-size:13px;">
        <div>
          <strong>Signature:</strong><br/>
          Patient / Guardian / Relation
        </div>
      </div>
    `;

      const fullImagePath = hospitalDetails?.logo ? `${this.staticimagepath}${hospitalDetails.logo}` : null;
      const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;

      const replacements: Record<string, string> = {
        reportTitle: `Admission Paper of ${this.patientinfo.firstName} ${this.patientinfo.lastName}`,
        hospitalName: this.defaultValuePipe.transform(hospitalDetails.name, ''),
        hospitalAddress: this.defaultValuePipe.transform(hospitalDetails.address, ''),
        phone: this.defaultValuePipe.transform(hospitalDetails.phone, ''),
        reportSections: admissionHtml,
        doctorName: doctorName,
        qualification: this.defaultValuePipe.transform(doctor?.result?.degree, '') ,
        doctorRegistrationNo: doctor?.result?.registrationNumber 
          ? `Reg No. ${this.defaultValuePipe.transform(doctor?.result?.registrationNumber, '')}` 
          : '',
        doctorPhone: doctor?.result?.phoneNumber 
          ? `M. ${this.defaultValuePipe.transform(doctor?.result?.phoneNumber, '')}` 
          : '',
        logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`,
        patientName: `${this.patientinfo.firstName} ${this.patientinfo.lastName}`,
        patientAge: `${this.getGenderName(this.patientinfo.gender)} / ${this.patientinfo.age}`,
        opdNumber: this.OpdData.opdNumber || '',
        opdDate: this.datePipe.transform(this.OpdData.opdDate, 'dd-MM-yyyy') || ''
      };

      let filledTemplate = template;
      for (const key in replacements) {
        const value = replacements[key] ?? '';
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        filledTemplate = filledTemplate.replace(regex, value);
      }

      // Apply settings
      if (settings) {
        const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
        const headerClass = settings.logoPosition === 'center' ? 'center' : '';
        const headerAlignment = settings.addressAlignment || 'left';

        const styles = `
            :root {
                --logo-size: ${settings.logoSize || '60px'};
                --logo-position: ${logoPos};
                --header-alignment: ${headerAlignment};
                --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                --address-font-size: ${settings.addressFontSize || 13}px;
                --footer-display: ${settings.showFooter ? 'block' : 'none'};
                --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
                --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
            }
        `;
        filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
      } else {
        filledTemplate = filledTemplate.replace('{{headerClass}}', '');
      }

      return filledTemplate;
    } catch (error) {
      console.error('Error creating admission paper print content:', error);
      throw error;
    }
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) {
      return;
    }

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadIpdData(): void {
    const ipdId = Number(this.route.snapshot.paramMap.get('ipdId'));
    if (!ipdId) return;

    this.loaderService.show();
    this.ipdService.getIPDById(ipdId).pipe(
      takeUntil(this.destroy$),
      tap(ipdRes => {
        this.ipdData = ipdRes.result;
      }),
      switchMap(ipdRes => {
        const opdId = ipdRes.result?.opdId;
        const doctorId = ipdRes.result?.doctorId;
        const currentIpdId = ipdRes.result?.id;

        return forkJoin({
          opd: opdId ? this.opdService.getOPDById(opdId) : of(null),
          doctor: doctorId ? this.userService.getUserByID(doctorId) : of(null),
          operationSummary: currentIpdId ? this.ipdService.getOperationSummaryByIpdId(currentIpdId).pipe(
            catchError(() => of({ result: null, isSuccess: false }))
          ) : of({ result: null, isSuccess: false })
        });
      }),
      finalize(() => {
        this.loaderService.hide();
      })
    ).subscribe({
      next: ({ opd, doctor, operationSummary }) => {
        if (opd) {
          this.OpdData = opd.result;
        }
        if (doctor) {
          this.doctorDetails = doctor.result;
        }
        if (operationSummary?.result) {
          this.operationSummary = operationSummary.result;
        } else {
          this.operationSummary = null;
        }
      },
      error: err => {
        console.error(err);
      }
    });
  }

  private loadPatient(): void {
    const patientId = Number(this.route.snapshot.paramMap.get('patientId'));
    if (!patientId) {
      return;
    }

    this.patientService.getPatientByID(patientId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          console.error('Error loading patient:', err);
          return of({ result: null });
        })
      )
      .subscribe(res => {
        if (res?.result) {
          this.patientinfo = res.result;
        }
      });
  }

  openOperationSummary() {
    if (!this.ipdData?.id) {
      return;
    }

    // Use already loaded operation summary data
    const isUpdateMode = this.operationSummary !== null;
    const existingOperationSummary = this.operationSummary || undefined;

    this.dialog.open(IpdOperationSummaryComponent, {
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        patientDetails: this.patientinfo,
        opdDetails: this.OpdData,
        ipdId: this.ipdData.id,
        isUpdateMode: isUpdateMode,
        existingOperationSummary: existingOperationSummary,
        readOnly: this.hasDischargeDate()
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        // Reload operation summary after successful save/update
        this.loadOperationSummary();
      }
    });
  }

  private loadOperationSummary(): void {
    if (!this.ipdData?.id) return;

    this.ipdService.getOperationSummaryByIpdId(this.ipdData.id)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.operationSummary = null;
          return of({ result: null, isSuccess: false });
        })
      )
      .subscribe({
        next: (response) => {
          if (response?.result) {
            this.operationSummary = response.result;
          } else {
            this.operationSummary = null;
          }
        }
      });
  }

  canGenerateBill(ipd: Iipd): boolean {
    const hasDischargeDate = (ipd as any).dischargeDate !== null && (ipd as any).dischargeDate !== undefined;
    return hasDischargeDate;
  }

  openBillDialog(): void {
    const ipdId = this.ipdData?.id;
    if (!ipdId) {
      return;
    }

    if (!this.canGenerateBill(this.ipdData)) {
      this.toastr.warning(
        this.translate.instant('MESSAGE.BILL_ONLY_AFTER_DISCHARGE')
      );
      return;
    }

    this.ipdService.getIPDById(ipdId).subscribe({
      next: ({ result }) => {
        this.ipdDetails = result;

        const patientName = [
          this.patientinfo?.firstName,
          this.patientinfo?.lastName
        ].filter(Boolean).join(' ');

        const ipdBillData: IPDBillData = {
          ipdId: ipdId,
          opdId: this.ipdDetails.opdId,
          ipdNumber: String(
            this.ipdDetails.ipdNumber ?? this.ipdDetails.ipdno ?? ''
          ),
          patientId: this.patientinfo.id,
          patientName,

          admissionDate: new Date(this.ipdDetails.ipdDate),
          dischargeDate: this.ipdDetails.dischargeDate
            ? new Date(this.ipdDetails.dischargeDate)
            : new Date(),

          roomChargeId:
            this.ipdDetails.roomChargeId ??
            this.ipdDetails.roomType?.chargeId ??
            this.ipdDetails.roomType?.billCharge?.id ??
            0,

          roomCharge:
            this.ipdDetails.roomCharge ??
            (this.ipdDetails.roomType?.billCharge?.charge
              ? parseFloat(this.ipdDetails.roomType.billCharge.charge)
              : 0),

          roomTypeName: this.ipdDetails.roomTypeName ?? this.ipdDetails.roomType?.name ?? '',
          wardName: this.ipdDetails.wardName ?? ''
        };

        const dialogData: BillDialogData = {
          billType: BillType.IPDBill,
          ipdBillData: ipdBillData,
          selectedDoctorId: this.ipdDetails.doctorId
        };

        this.dialog.open(AddBillChargeComponent, {
          autoFocus: false,
          position: { top: '0', right: '0' },
          height: '100vh',
          width: '82vw',
          maxWidth: '1400px',
          panelClass: ['bill-dialog', 'side-panel'],
          data: dialogData
        });
      },

      error: () => {
        this.toastr.error(
          this.translate.instant('MESSAGE.ERROR_LOADING_IPD_DETAILS')
        );
      }
    });
  }
  openDischargeCard() {
    if (!this.ipdData || !this.ipdData.id) {
      return;
    }

    const dialogRef = this.dialog.open(IpdDischargeComponent, {
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        patientDetails: this.patientinfo,
        opdDetails: this.OpdData,
        ipdId: this.ipdData.id,
        ipdData: this.ipdData,
        readOnly: this.hasDischargeDate(),
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        // Reload IPD data after successful update
        this.loadIpdData();
      }
    });
  }

  hasDischargeDate(): boolean {
    return !!(this.ipdData?.dischargeDate && this.ipdData?.dischargeTime);
  }

  openPrescription() {
    const dialogRef = this.dialog.open(PrescriptionComponent, {
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        patientDetails: this.patientinfo,
        opdDetails: this.OpdData,
        ipdId: this.ipdData.id,
        ipdData: this.ipdData,
        doctorData: this.doctorDetails,
        isDialog: true
      }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        // Reload IPD data after successful update
        this.loadIpdData();
      }
    });
  }

  openMedicalCertificate() {
    const dialogRef = this.dialog.open(MedicalCertificateComponent, {
      height: 'auto',
      minWidth: '45vw',
      minHeight: '50vh',
      disableClose: true,
      data: {
        patientDetails: this.patientinfo,
        opdDetails: this.OpdData,
        ipdData: this.ipdData,
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      // if (result) {
      //   // Reload IPD data after successful update
      //   this.loadIpdData();
      // }
    });
  }

  viewOPD(row: Iopd): void {
    if (row.patientId && row.id) {
      this.router.navigate(['/opd', row.patientId, 'consultation', row.id]);
    }
  }

  viewPatientDetails(element: IPatientInfo): void {
    this.dialog.open(PatientViewComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      data: {
        patient: element,
      }
    }).afterClosed().subscribe();
  }

  printDischarge() {
    this.reportPrintComponent?.printDischargeDetails();
  }

  printIPDAdmissionPaper() {
    this.printAdmissionPaperWithCurrentData();
  }

  openMedicalRecord() {

    this.dialog.open(MedicalRecordDetailComponent, {
      width: '100vw',
      maxWidth: '600px',
      disableClose: true,
      data: { 
        patientId: this.patientinfo.id,
        defaultType: MedicalReportType.IPD 
      }
    })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
         if (res && this.medicalRecordComponent) {
            this.medicalRecordComponent.loadMedicalRecords();
         }
      });
  }

  onMedicalRecordLength(count: number): void {
    this.medicalRecordCount = count;
  }

}
